import { ProductList, StatusEnum } from '@shared/api/types';
import Text from '@shared/components/content/text';
import Unit from '@shared/components/content/unit';
import { BadgeCell } from '@shared/components/data-table/cells/badge-cell';
import { DateCell } from '@shared/components/data-table/cells/date-cell';
import { HeaderCell } from '@shared/components/data-table/cells/header-cell';
import { NumberCell } from '@shared/components/data-table/cells/number-cell';
import RatingCloud, {
  RatingCloudProps,
} from '@shared/components/ratings/rating-cloud';
import { Checkbox } from '@shared/components/ui/checkbox';
import { capitalizeFirstLetter } from '@shared/lib/utils';
import { ColumnDef } from '@tanstack/react-table';
import { ProductTableRowActions } from './product-table-row-actions';

export function getProductTableColumns(): ColumnDef<ProductList>[] {
  return [
    {
      accessorKey: 'uuid',
      show: false,
      enableHiding: true,
    },
    {
      id: 'select',
      enableHiding: true,
      header: ({ table, column }) => (
        <HeaderCell
          column={column}
          name={
            <Checkbox
              checked={
                table.getIsAllPageRowsSelected() ||
                (table.getIsSomePageRowsSelected() && 'indeterminate')
              }
              onCheckedChange={(value) =>
                table.toggleAllPageRowsSelected(!!value)
              }
              aria-label="Select all"
              className="my-auto translate-y-0.5 place-self-center border border-primary-200 bg-gray-00 text-gray-00"
            />
          }
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-0.5"
        />
      ),
      enableSorting: false,
    },
    {
      accessorKey: 'name',
      meta: {
        label: 'Name',
        sorting: {
          ascLabel: 'Name (A-Z)',
          descLabel: 'Name (Z-A)',
        },
      },
      header: ({ column }) => (
        <div data-testid="product-column-header">
          <HeaderCell column={column} name="Product" className="text-gray-00" />
        </div>
      ),
      cell: ({ getValue }) => {
        const name = getValue<string>();
        return (
          <Text
            className="min-w-[150px] max-w-[240px] truncate"
            includeHover
            data-testid="product-name-cell"
          >
            {name}
          </Text>
        );
      },
      enableHiding: false,
    },
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <HeaderCell column={column} name="Status" textWhite />
      ),
      cell: ({ getValue }) => {
        const status = getValue<StatusEnum>();
        return (
          <BadgeCell
            text={
              status === 'in progress'
                ? 'Submitted'
                : capitalizeFirstLetter(status)
            }
          />
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: 'assessmentDate',
      meta: {
        label: 'Assessment Date',
        sorting: {
          ascLabel: 'Assessment Date (Oldest first)',
          descLabel: 'Assessment Date (Newest first)',
        },
      },
      enableSorting: true,
      sortingFn: 'alphanumeric',
      sortUndefined: 1,
      header: ({ column }) => (
        <HeaderCell column={column} name="Assessment Date" textWhite />
      ),
      cell: ({ getValue }) => {
        const date = getValue<string>();
        return (date && <DateCell date={date} />) ?? null;
      },
    },
    {
      accessorKey: 'totalEmissions',
      meta: {
        label: 'Total Emissions',
        sorting: {
          ascLabel: 'Total Emissions (Lowest first)',
          descLabel: 'Total Emissions (Highest first)',
        },
      },
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Total Emissions"
          numberColumn
          unit={<Unit variant="gCO2e" className="text-gray-00" />}
          textWhite
        />
      ),
      cell: ({ getValue }) => {
        const totalEmissions = getValue<number>();

        return (
          (totalEmissions && <NumberCell number={totalEmissions! * 1000} />) ??
          null
        );
      },
    },
    {
      accessorKey: 'totalEmissionsPerKg',
      meta: {
        label: 'Carbon Intensity',
        sorting: {
          ascLabel: 'Carbon Intensity (Lowest first)',
          descLabel: 'Carbon Intensity (Highest first)',
        },
      },
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Carbon Intensity"
          numberColumn
          unit={<Unit variant="kgCO2e/kg" className="text-gray-00" />}
          textWhite
        />
      ),
      cell: ({ getValue }) => {
        const totalEmissions: number = getValue<number>();
        return (
          (totalEmissions && <NumberCell number={totalEmissions} />) ?? null
        );
      },
    },
    {
      accessorKey: 'emissionsCategory',
      header: ({ column }) => (
        <HeaderCell column={column} name="Rating" textWhite />
      ),
      cell: ({ getValue, row }) => {
        const rating = getValue<RatingCloudProps['rating']>();
        const status = row.getValue<StatusEnum>('status');
        return (
          (rating && (
            <RatingCloud
              rating={rating as RatingCloudProps['rating']}
              status={status}
            />
          )) ??
          null
        );
      },
      enableSorting: false,
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        return <ProductTableRowActions product={row.original} />;
      },
    },
  ] as ColumnDef<ProductList>[];
}
